<template>
  <div class="onboard-page">
    <!-- Background Elements -->
    <div class="page-grid"></div>
    <div class="page-orb orb-1"></div>
    <div class="page-orb orb-2"></div>

    <!-- Header -->
    <div class="onboard-header">
      <div class="header-content">
        <div class="header-brand">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
            <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span>Onboard New Customer</span>
        </div>
        <button class="btn-back" @click="goBack">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M15.8333 10H4.16667M4.16667 10L10 15.8333M4.16667 10L10 4.16667" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span>Back</span>
        </button>
      </div>
    </div>

    <v-container class="onboard-container">
      <!-- Hero Section -->
      <div class="onboard-hero">
        <h1 class="hero-title">Let's set up your customer</h1>
        <p class="hero-subtitle">Guided steps. Saved to Firestore. Ready to win.</p>
      </div>

      <!-- Main Content -->
      <div class="content-grid">
        <!-- Wizard Card -->
        <div class="wizard-card">
          <!-- Stepper Header -->
          <div class="stepper-header">
            <div 
              v-for="(stepInfo, idx) in steps" 
              :key="idx"
              :class="['step-item', { 
                active: step === idx + 1, 
                complete: step > idx + 1 
              }]"
              @click="step = idx + 1"
            >
              <div class="step-number">
                <svg v-if="step > idx + 1" width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M13.3333 4L6 11.3333L2.66667 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span v-else>{{ idx + 1 }}</span>
              </div>
              <div class="step-label">{{ stepInfo.label }}</div>
            </div>
          </div>

          <!-- Step Content -->
          <div class="step-content">
            <!-- Step 1: Account -->
            <div v-if="step === 1" class="step-panel">
              <div class="panel-header">
                <h2>Account Credentials</h2>
                <p>Set up login credentials for your customer</p>
              </div>

              <div class="form-field">
                <label>Customer Email *</label>
                <div class="input-wrapper">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M2.5 6.66667L9.0755 11.0504C9.63533 11.4236 10.3647 11.4236 10.9245 11.0504L17.5 6.66667M4.16667 15H15.8333C16.7538 15 17.5 14.2538 17.5 13.3333V6.66667C17.5 5.74619 16.7538 5 15.8333 5H4.16667C3.24619 5 2.5 5.74619 2.5 6.66667V13.3333C2.5 14.2538 3.24619 15 4.16667 15Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <input v-model="customerEmail" type="email" placeholder="customer@example.com" />
                </div>
              </div>

              <div class="form-field">
                <label>Temporary Password *</label>
                <div class="input-wrapper">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M5.83333 9.16667V6.66667C5.83333 4.36548 7.69881 2.5 10 2.5C12.3012 2.5 14.1667 4.36548 14.1667 6.66667V9.16667M10 11.6667V13.3333M6.66667 17.5H13.3333C14.2538 17.5 15 16.7538 15 15.8333V10.8333C15 9.91286 14.2538 9.16667 13.3333 9.16667H6.66667C5.74619 9.16667 5 9.91286 5 10.8333V15.8333C5 16.7538 5.74619 17.5 6.66667 17.5Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <input 
                    v-model="customerPassword" 
                    :type="showPwd ? 'text' : 'password'" 
                    placeholder="Min 6 characters"
                  />
                  <button class="input-toggle" @click="showPwd = !showPwd">
                    <svg v-if="!showPwd" width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M2.5 10C2.5 10 5 4.16667 10 4.16667C15 4.16667 17.5 10 17.5 10C17.5 10 15 15.8333 10 15.8333C5 15.8333 2.5 10 2.5 10Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M10 12.5C11.3807 12.5 12.5 11.3807 12.5 10C12.5 8.61929 11.3807 7.5 10 7.5C8.61929 7.5 7.5 8.61929 7.5 10C7.5 11.3807 8.61929 12.5 10 12.5Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    <svg v-else width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M8.33333 8.33333C8.11232 8.69493 8 9.11497 8 9.58333C8 10.9641 9.11929 12.0833 10.5 12.0833C10.9684 12.0833 11.3884 11.971 11.75 11.75M14.95 14.95C13.5255 16.0358 11.8283 16.6667 10 16.6667C5 16.6667 2.5 10.8333 2.5 10.8333C3.47824 8.90809 4.80672 7.30515 6.38333 6.05M8.25 3.80833C8.82379 3.67954 9.40923 3.61484 10 3.61667C15 3.61667 17.5 9.45 17.5 9.45C17.0176 10.3891 16.446 11.2642 15.7917 12.0667M2.5 2.5L17.5 17.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </button>
                </div>
                <div class="password-strength">
                  <div class="strength-bar">
                    <div class="strength-fill" :style="{ width: passwordStrength + '%' }"></div>
                  </div>
                  <span class="strength-text">Password strength: {{ strengthLabel }}</span>
                </div>
              </div>

              <button class="btn-generate" @click="genPassword">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M2.25 9H15.75M9 2.25V15.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                Generate Strong Password
              </button>

              <div class="step-actions">
                <button class="btn-next" @click="step = 2" :disabled="!canStep1">
                  Continue
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M6.75 13.5L11.25 9L6.75 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
              </div>
            </div>

            <!-- Step 2: Personal -->
            <div v-if="step === 2" class="step-panel">
              <div class="panel-header">
                <h2>Personal Information</h2>
                <p>Add contact details for this customer</p>
              </div>

              <div class="form-field">
                <label>Full Name</label>
                <input v-model="fullName" type="text" placeholder="John Smith" />
              </div>

              <div class="form-field">
                <label>Phone Number</label>
                <input v-model="phone" type="tel" placeholder="+1 (555) 123-4567" />
              </div>

              <div class="form-field">
                <label>Website</label>
                <input v-model="website" type="url" placeholder="https://example.com" />
              </div>

              <div class="step-actions">
                <button class="btn-back-step" @click="step = 1">
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M11.25 13.5L6.75 9L11.25 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  Back
                </button>
                <button class="btn-next" @click="step = 3">
                  Continue
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M6.75 13.5L11.25 9L6.75 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
              </div>
            </div>

            <!-- Step 3: Business -->
            <div v-if="step === 3" class="step-panel">
              <div class="panel-header">
                <h2>Business Details</h2>
                <p>Tell us about their business</p>
              </div>

              <div class="form-field">
                <label>Business Name *</label>
                <input v-model="businessName" type="text" placeholder="Acme Corporation" />
              </div>

              <div class="form-field">
                <label>Business Description</label>
                <textarea v-model="businessDescription" rows="3" placeholder="Brief description of the business..."></textarea>
              </div>

              <div class="step-actions">
                <button class="btn-back-step" @click="step = 2">
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M11.25 13.5L6.75 9L11.25 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  Back
                </button>
                <button class="btn-next" @click="step = 4">
                  Continue
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M6.75 13.5L11.25 9L6.75 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
              </div>
            </div>

            <!-- Step 4: Address -->
            <div v-if="step === 4" class="step-panel">
              <div class="panel-header">
                <h2>Address</h2>
                <p>Where is this business located?</p>
              </div>

              <div class="form-field">
                <label>Street Address *</label>
                <input v-model="street" type="text" placeholder="123 Main Street" />
              </div>

              <div class="form-row">
                <div class="form-field">
                  <label>City *</label>
                  <input v-model="city" type="text" placeholder="New York" />
                </div>
                <div class="form-field">
                  <label>State *</label>
                  <input v-model="state" type="text" placeholder="NY" />
                </div>
              </div>

              <div class="form-row">
                <div class="form-field">
                  <label>ZIP Code *</label>
                  <input v-model="zip" type="text" placeholder="10001" />
                </div>
                <div class="form-field">
                  <label>Country *</label>
                  <input v-model="country" type="text" placeholder="USA" />
                </div>
              </div>

              <div class="step-actions">
                <button class="btn-back-step" @click="step = 3">
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M11.25 13.5L6.75 9L11.25 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  Back
                </button>
                <button class="btn-next" @click="step = 5">
                  Continue
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M6.75 13.5L11.25 9L6.75 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
              </div>
            </div>

            <!-- Step 5: Billing -->
            <div v-if="step === 5" class="step-panel">
              <div class="panel-header">
                <h2>Billing & Preferences</h2>
                <p>Optional billing info and customization</p>
              </div>

              <div class="expandable-section">
                <button class="expand-header" @click="billingExpanded = !billingExpanded">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M4.16667 6.66667H15.8333M4.16667 6.66667C3.24619 6.66667 2.5 6.10847 2.5 5.41667V3.75C2.5 3.05814 3.24619 2.5 4.16667 2.5H15.8333C16.7538 2.5 17.5 3.05814 17.5 3.75V5.41667C17.5 6.10847 16.7538 6.66667 15.8333 6.66667M4.16667 6.66667C3.24619 6.66667 2.5 7.22486 2.5 7.91667V16.25C2.5 16.9419 3.24619 17.5 4.16667 17.5H15.8333C16.7538 17.5 17.5 16.9419 17.5 16.25V7.91667C17.5 7.22486 16.7538 6.66667 15.8333 6.66667M4.16667 10H5.83333M4.16667 13.3333H5.83333" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <span>Billing Details (Optional)</span>
                  <svg :class="['expand-icon', { expanded: billingExpanded }]" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
                <div v-if="billingExpanded" class="expand-content">
                  <div class="form-field">
                    <label>Billing Name</label>
                    <input v-model="billingName" type="text" placeholder="John Smith" />
                  </div>
                  <div class="form-field">
                    <label>Card Number</label>
                    <input v-model="cardNumber" type="text" placeholder="•••• •••• •••• 1234" />
                  </div>
                  <div class="form-row">
                    <div class="form-field">
                      <label>CVV</label>
                      <input v-model="cvv" type="text" placeholder="123" maxlength="4" />
                    </div>
                    <div class="form-field">
                      <label>Expiry (MM/YY)</label>
                      <input v-model="expiry" type="text" placeholder="12/25" />
                    </div>
                  </div>
                  <div class="info-box">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <path d="M9 15.75C12.7279 15.75 15.75 12.7279 15.75 9C15.75 5.27208 12.7279 2.25 9 2.25C5.27208 2.25 2.25 5.27208 2.25 9C2.25 12.7279 5.27208 15.75 9 15.75Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M9 6V9M9 12H9.0075" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    <span>You can skip billing during trials and add later</span>
                  </div>
                </div>
              </div>

              <div class="expandable-section">
                <button class="expand-header" @click="prefsExpanded = !prefsExpanded">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M4.16667 10.8333C4.16667 10.8333 5.83333 7.5 10 7.5C14.1667 7.5 15.8333 10.8333 15.8333 10.8333M2.5 3.75L17.5 3.75M2.5 16.25L17.5 16.25" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <span>Preferences (Optional)</span>
                  <svg :class="['expand-icon', { expanded: prefsExpanded }]" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
                <div v-if="prefsExpanded" class="expand-content">
                  <div class="form-field">
                    <label>Theme Color</label>
                    <input v-model="colorPreference" type="text" placeholder="e.g., blue, purple" />
                  </div>
                  <div class="form-field">
                    <label>Languages Supported</label>
                    <div class="language-tags">
                      <span 
                        v-for="(lang, idx) in languages" 
                        :key="idx" 
                        class="language-tag"
                      >
                        {{ lang }}
                        <button @click="removeLanguage(idx)">✕</button>
                      </span>
                      <input 
                        v-model="newLanguage"
                        type="text" 
                        placeholder="Add language..."
                        @keydown.enter="addLanguage"
                        class="language-input"
                      />
                    </div>
                  </div>
                  <div class="form-field">
                    <label>Additional Notes</label>
                    <textarea v-model="additionalNotes" rows="3" placeholder="Any other information..."></textarea>
                  </div>
                </div>
              </div>

              <div class="step-actions">
                <button class="btn-back-step" @click="step = 4">
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M11.25 13.5L6.75 9L11.25 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  Back
                </button>
                <button class="btn-next" @click="step = 6">
                  Review
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M6.75 13.5L11.25 9L6.75 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
              </div>
            </div>

            <!-- Step 6: Review -->
            <div v-if="step === 6" class="step-panel">
              <div class="panel-header">
                <h2>Review & Create</h2>
                <p>Verify all information before creating the account</p>
              </div>

              <div class="review-grid">
                <div class="review-section">
                  <div class="review-title">Account</div>
                  <div class="review-item">
                    <span class="review-label">Email:</span>
                    <span class="review-value">{{ customerEmail }}</span>
                  </div>
                </div>

                <div class="review-section">
                  <div class="review-title">Personal</div>
                  <div class="review-item">
                    <span class="review-label">Name:</span>
                    <span class="review-value">{{ fullName || '—' }}</span>
                  </div>
                  <div class="review-item">
                    <span class="review-label">Phone:</span>
                    <span class="review-value">{{ phone || '—' }}</span>
                  </div>
                  <div class="review-item">
                    <span class="review-label">Website:</span>
                    <span class="review-value">{{ website || '—' }}</span>
                  </div>
                </div>

                <div class="review-section">
                  <div class="review-title">Business</div>
                  <div class="review-item">
                    <span class="review-label">Name:</span>
                    <span class="review-value">{{ businessName || '—' }}</span>
                  </div>
                  <div class="review-item">
                    <span class="review-label">Description:</span>
                    <span class="review-value">{{ businessDescription || '—' }}</span>
                  </div>
                </div>

                <div class="review-section">
                  <div class="review-title">Address</div>
                  <div class="review-item">
                    <span class="review-value">
                      {{ street }}, {{ city }}, {{ state }} {{ zip }}, {{ country }}
                    </span>
                  </div>
                </div>
              </div>

              <div v-if="message" :class="['message-alert', messageType]">
                <svg v-if="messageType === 'success'" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M16.6667 5L7.50004 14.1667L3.33337 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <svg v-else width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M10 18.3333C14.6024 18.3333 18.3333 14.6024 18.3333 10C18.3333 5.39763 14.6024 1.66667 10 1.66667C5.39763 1.66667 1.66667 5.39763 1.66667 10C1.66667 14.6024 5.39763 18.3333 10 18.3333Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M10 6.66667V10M10 13.3333H10.0083" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span>{{ message }}</span>
              </div>

              <div class="step-actions">
                <button class="btn-back-step" @click="step = 5">
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M11.25 13.5L6.75 9L11.25 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  Back
                </button>
                <button class="btn-create" @click="submitOnboarding" :disabled="submitting">
                  <span v-if="!submitting">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <path d="M15 4.5L6.75 12.75L3 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    Create Customer Account
                  </span>
                  <span v-else class="loader-dots">
                    <span></span><span></span><span></span>
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Tips Sidebar -->
        <div class="tips-card">
          <div class="tips-header">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M15 12C15 13.6569 13.6569 15 12 15C10.3431 15 9 13.6569 9 12C9 10.3431 10.3431 9 12 9C13.6569 9 15 10.3431 15 12Z" stroke="currentColor" stroke-width="2"/>
              <path d="M12 2V4M12 20V22M4 12H2M6.31412 6.31412L4.8999 4.8999M17.6859 6.31412L19.1001 4.8999M6.31412 17.69L4.8999 19.1042M17.6859 17.69L19.1001 19.1042M22 12H20" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
            <span>Success Tips</span>
          </div>

          <div class="tips-list">
            <div class="tip-item">
              <div :class="['tip-icon', { active: canStep1 }]">
                <svg v-if="canStep1" width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M15 4.5L6.75 12.75L3 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span v-else>1</span>
              </div>
              <span class="tip-text">Email & strong password set</span>
            </div>

            <div class="tip-item">
              <div :class="['tip-icon', { active: businessName }]">
                <svg v-if="businessName" width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M15 4.5L6.75 12.75L3 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span v-else>2</span>
              </div>
              <span class="tip-text">Business name provided</span>
            </div>

            <div class="tip-item">
              <div :class="['tip-icon', { active: addressComplete }]">
                <svg v-if="addressComplete" width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M15 4.5L6.75 12.75L3 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span v-else>3</span>
              </div>
              <span class="tip-text">Address complete</span>
            </div>

            <div class="tip-item">
              <div class="tip-icon tip-icon-optional">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M9 4.5V9L12 10.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M9 15.75C12.7279 15.75 15.75 12.7279 15.75 9C15.75 5.27208 12.7279 2.25 9 2.25C5.27208 2.25 2.25 5.27208 2.25 9C2.25 12.7279 5.27208 15.75 9 15.75Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
              <span class="tip-text">Choose theme & languages</span>
            </div>
          </div>

          <div class="tips-footer">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M9 15.75C12.7279 15.75 15.75 12.7279 15.75 9C15.75 5.27208 12.7279 2.25 9 2.25C5.27208 2.25 2.25 5.27208 2.25 9C2.25 12.7279 5.27208 15.75 9 15.75Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M9 6V9M9 12H9.0075" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span>You can skip billing during trials and add later</span>
          </div>
        </div>
      </div>
    </v-container>
  </div>
</template>

<script>
import { auth, db } from '../firebase'
import { createUserWithEmailAndPassword } from 'firebase/auth'
import { doc, setDoc, serverTimestamp } from 'firebase/firestore'
import router from '../router'

export default {
  name: 'StaffOnboardCustomer',
  data() {
    return {
      step: 1,
      steps: [
        { label: 'Account' },
        { label: 'Personal' },
        { label: 'Business' },
        { label: 'Address' },
        { label: 'Billing' },
        { label: 'Review' }
      ],

      // Form fields
      customerEmail: '',
      customerPassword: '',
      showPwd: false,
      fullName: '',
      phone: '',
      website: '',
      businessName: '',
      businessDescription: '',
      street: '',
      city: '',
      state: '',
      zip: '',
      country: '',
      billingName: '',
      cardNumber: '',
      cvv: '',
      expiry: '',
      colorPreference: '',
      languages: [],
      newLanguage: '',
      additionalNotes: '',

      // UI state
      submitting: false,
      message: '',
      messageType: 'success',
      billingExpanded: false,
      prefsExpanded: false
    }
  },
  computed: {
    passwordStrength() {
      const v = this.customerPassword || ''
      let score = 0
      if (v.length >= 6) score += 25
      if (/[A-Z]/.test(v)) score += 25
      if (/[0-9]/.test(v)) score += 25
      if (/[^A-Za-z0-9]/.test(v)) score += 25
      return score
    },
    strengthLabel() {
      const s = this.passwordStrength
      if (s === 0) return 'None'
      if (s <= 25) return 'Weak'
      if (s <= 50) return 'Fair'
      if (s <= 75) return 'Good'
      return 'Strong'
    },
    canStep1() {
      return this.customerEmail && 
             /.+@.+\..+/.test(this.customerEmail) && 
             this.customerPassword && 
             this.customerPassword.length >= 6
    },
    addressComplete() {
      return this.street && this.city && this.state && this.zip && this.country
    }
  },
  methods: {
    genPassword() {
      const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%&*?'
      let out = ''
      for (let i = 0; i < 12; i++) {
        out += chars[Math.floor(Math.random() * chars.length)]
      }
      this.customerPassword = out
    },
    addLanguage() {
      if (this.newLanguage && !this.languages.includes(this.newLanguage)) {
        this.languages.push(this.newLanguage)
        this.newLanguage = ''
      }
    },
    removeLanguage(idx) {
      this.languages.splice(idx, 1)
    },
    async submitOnboarding() {
      if (!this.customerEmail || !this.customerPassword || !this.businessName || !this.addressComplete) {
        this.message = 'Please complete all required fields'
        this.messageType = 'error'
        return
      }

      this.submitting = true
      try {
        const userCredential = await createUserWithEmailAndPassword(
          auth, 
          this.customerEmail, 
          this.customerPassword
        )
        const customerUid = userCredential.user.uid

        const userData = {
          uid: customerUid,
          email: this.customerEmail,
          role: 'Customer',
          fullName: this.fullName,
          phone: this.phone,
          website: this.website,
          businessName: this.businessName,
          businessDescription: this.businessDescription,
          address: {
            street: this.street,
            city: this.city,
            state: this.state,
            zip: this.zip,
            country: this.country
          },
          billing: {
            name: this.billingName,
            cardNumber: this.cardNumber,
            cvv: this.cvv,
            expiry: this.expiry
          },
          colorPreference: this.colorPreference,
          languages: this.languages,
          additionalNotes: this.additionalNotes,
          appProgress: 'Not Started',
          createdAt: serverTimestamp()
        }

        await setDoc(doc(db, 'users', customerUid), userData)
        await setDoc(doc(db, 'customers', customerUid), userData)

        this.message = '✅ Customer account created successfully!'
        this.messageType = 'success'

        setTimeout(() => {
          router.push('/staff-dashboard')
        }, 2000)
      } catch (error) {
        console.error('Error onboarding customer:', error)
        this.message = '❌ Failed to create account: ' + error.message
        this.messageType = 'error'
      } finally {
        this.submitting = false
      }
    },
    goBack() {
      router.push('/staff-dashboard')
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Space+Mono:wght@400;700&family=Sora:wght@300;400;500;600;700;800&display=swap');

:root {
  --color-bg: #0a0118;
  --color-surface: #1a0b2e;
  --color-text: #ffffff;
  --color-text-muted: #b8b8d1;
  --color-accent: #00fff5;
  --color-accent-2: #7b2ff7;
  --color-pink: #ff006e;
  --color-yellow: #ffbe0b;
  --font-display: 'Sora', sans-serif;
  --font-mono: 'Space Mono', monospace;
}

* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

/* Page Background */
.onboard-page {
  min-height: 100vh;
  background: 
    radial-gradient(circle at 15% 20%, rgba(123, 47, 247, 0.3), transparent 40%),
    radial-gradient(circle at 85% 80%, rgba(255, 0, 110, 0.3), transparent 40%),
    radial-gradient(circle at 50% 50%, rgba(0, 255, 245, 0.2), transparent 50%),
    linear-gradient(135deg, #1a0b2e 0%, #0a0118 50%, #1a0b2e 100%);
  background-size: 200% 200%;
  animation: bg-breathe 25s ease-in-out infinite;
  position: relative;
  font-family: var(--font-display);
  padding-bottom: 80px;
}

@keyframes bg-breathe {
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
}

.page-grid {
  position: fixed;
  inset: 0;
  background-image: 
    linear-gradient(to right, rgba(0, 255, 245, 0.08) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(123, 47, 247, 0.08) 1px, transparent 1px);
  background-size: 60px 60px;
  mask-image: radial-gradient(ellipse 70% 60% at 50% 50%, black 20%, transparent 70%);
  animation: grid-flow 35s linear infinite;
  pointer-events: none;
}

@keyframes grid-flow {
  0% { transform: translate(0, 0); }
  100% { transform: translate(60px, 60px); }
}

.page-orb {
  position: fixed;
  border-radius: 50%;
  filter: blur(140px);
  opacity: 0.5;
  animation: orb-float 18s ease-in-out infinite;
  mix-blend-mode: screen;
  pointer-events: none;
}

.orb-1 {
  width: 600px;
  height: 600px;
  background: radial-gradient(circle, var(--color-accent), transparent);
  top: -200px;
  left: -200px;
}

.orb-2 {
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, var(--color-pink), transparent);
  bottom: -150px;
  right: -150px;
  animation-delay: 6s;
}

@keyframes orb-float {
  0%, 100% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(50px, -50px) scale(1.1); }
  66% { transform: translate(-40px, 40px) scale(0.9); }
}

/* Header */
.onboard-header {
  position: sticky;
  top: 0;
  z-index: 100;
  background: linear-gradient(135deg, rgba(123, 47, 247, 0.95), rgba(255, 0, 110, 0.95));
  backdrop-filter: blur(20px) saturate(180%);
  border-bottom: 2px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 4px 30px rgba(123, 47, 247, 0.4);
}

.header-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 16px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.header-brand {
  display: flex;
  align-items: center;
  gap: 12px;
  color: #ffffff;
  font-size: 18px;
  font-weight: 700;
}

.header-brand svg {
  filter: drop-shadow(0 0 8px var(--color-accent));
}

.btn-back {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-back:hover {
  background: rgba(255, 255, 255, 0.15);
  transform: translateX(-2px);
}

/* Container */
.onboard-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 40px 24px;
  position: relative;
  z-index: 2;
}

/* Hero */
.onboard-hero {
  text-align: center;
  margin-bottom: 48px;
}

.hero-title {
  font-size: clamp(28px, 4vw, 42px);
  font-weight: 900;
  color: #ffffff;
  margin-bottom: 12px;
  letter-spacing: -0.02em;
  background: linear-gradient(135deg, var(--color-accent), var(--color-accent-2), var(--color-pink));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  background-size: 200% 200%;
  animation: gradient-shift 4s ease infinite;
}

@keyframes gradient-shift {
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
}

.hero-subtitle {
  font-size: 16px;
  color: var(--color-text-muted);
}

/* Content Grid */
.content-grid {
  display: grid;
  grid-template-columns: 1fr 380px;
  gap: 32px;
  align-items: start;
}

/* Wizard Card */
.wizard-card {
  background: rgba(255, 255, 255, 0.05);
  border: 2px solid var(--color-accent);
  border-radius: 20px;
  backdrop-filter: blur(20px);
  box-shadow: 0 0 40px rgba(0, 255, 245, 0.3);
  overflow: hidden;
}

/* Stepper Header */
.stepper-header {
  display: flex;
  padding: 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  gap: 8px;
  overflow-x: auto;
}

.step-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 80px;
}

.step-number {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  font-family: var(--font-mono);
  background: rgba(255, 255, 255, 0.05);
  border: 2px solid rgba(255, 255, 255, 0.2);
  color: var(--color-text-muted);
  transition: all 0.3s ease;
}

.step-item.active .step-number {
  background: linear-gradient(135deg, var(--color-accent), var(--color-accent-2));
  border-color: var(--color-accent);
  color: #ffffff;
  box-shadow: 0 0 20px rgba(0, 255, 245, 0.5);
}

.step-item.complete .step-number {
  background: rgba(34, 197, 94, 0.2);
  border-color: #4ade80;
  color: #4ade80;
}

.step-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--color-text-muted);
  text-align: center;
}

.step-item.active .step-label {
  color: var(--color-accent);
}

.step-item.complete .step-label {
  color: #4ade80;
}

/* Step Content */
.step-content {
  padding: 32px;
}

.step-panel {
  animation: panel-fade-in 0.4s ease;
}

@keyframes panel-fade-in {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.panel-header {
  margin-bottom: 32px;
}

.panel-header h2 {
  font-size: 24px;
  font-weight: 800;
  color: #ffffff;
  margin-bottom: 8px;
}

.panel-header p {
  font-size: 14px;
  color: var(--color-text-muted);
}

/* Form Fields */
.form-field {
  margin-bottom: 20px;
}

.form-field label {
  display: block;
  font-size: 13px;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 8px;
  font-family: var(--font-mono);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.input-wrapper svg {
  position: absolute;
  left: 14px;
  color: var(--color-accent);
  pointer-events: none;
}

.input-wrapper input {
  width: 100%;
  padding: 14px 14px 14px 46px;
  background: rgba(255, 255, 255, 0.08);
  border: 2px solid rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  color: #ffffff;
  font-family: var(--font-display);
  font-size: 15px;
  outline: none;
  transition: all 0.3s ease;
}

.input-wrapper input:focus {
  background: rgba(255, 255, 255, 0.12);
  border-color: var(--color-accent);
  box-shadow: 0 0 20px rgba(0, 255, 245, 0.3);
}

.input-wrapper input::placeholder {
  color: rgba(255, 255, 255, 0.5);
}

.input-toggle {
  position: absolute;
  right: 14px;
  background: transparent;
  border: none;
  color: var(--color-text-muted);
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  transition: color 0.2s ease;
}

.input-toggle:hover {
  color: var(--color-accent);
}

.form-field input,
.form-field textarea {
  width: 100%;
  padding: 14px;
  background: rgba(255, 255, 255, 0.08);
  border: 2px solid rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  color: #ffffff;
  font-family: var(--font-display);
  font-size: 15px;
  outline: none;
  transition: all 0.3s ease;
}

.form-field input:focus,
.form-field textarea:focus {
  background: rgba(255, 255, 255, 0.12);
  border-color: var(--color-accent);
  box-shadow: 0 0 20px rgba(0, 255, 245, 0.3);
}

.form-field input::placeholder,
.form-field textarea::placeholder {
  color: rgba(255, 255, 255, 0.5);
}

.form-field textarea {
  resize: vertical;
  min-height: 80px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

/* Password Strength */
.password-strength {
  margin-top: 8px;
}

.strength-bar {
  height: 6px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: 6px;
}

.strength-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--color-pink), var(--color-yellow));
  border-radius: 3px;
  transition: width 0.3s ease;
}

.strength-text {
  font-size: 12px;
  color: var(--color-text-muted);
  font-family: var(--font-mono);
}

/* Generate Button */
.btn-generate {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: rgba(255, 255, 255, 0.05);
  border: 2px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 24px;
}

.btn-generate:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: var(--color-accent);
}

/* Expandable Sections */
.expandable-section {
  margin-bottom: 16px;
  border: 2px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  overflow: hidden;
}

.expand-header {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: rgba(255, 255, 255, 0.03);
  border: none;
  color: #ffffff;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.expand-header:hover {
  background: rgba(255, 255, 255, 0.05);
}

.expand-icon {
  margin-left: auto;
  transition: transform 0.3s ease;
}

.expand-icon.expanded {
  transform: rotate(180deg);
}

.expand-content {
  padding: 16px;
  animation: expand-in 0.3s ease;
}

@keyframes expand-in {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.info-box {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  background: rgba(0, 255, 245, 0.1);
  border: 1px solid rgba(0, 255, 245, 0.3);
  border-radius: 8px;
  color: var(--color-accent);
  font-size: 13px;
  margin-top: 16px;
}

/* Language Tags */
.language-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 10px;
  background: rgba(255, 255, 255, 0.08);
  border: 2px solid rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  min-height: 48px;
}

.language-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: linear-gradient(135deg, var(--color-accent-2), var(--color-pink));
  border-radius: 6px;
  font-size: 13px;
  color: #ffffff;
  font-weight: 600;
}

.language-tag button {
  background: transparent;
  border: none;
  color: #ffffff;
  cursor: pointer;
  padding: 0 2px;
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.language-tag button:hover {
  opacity: 1;
}

.language-input {
  flex: 1;
  min-width: 120px;
  background: transparent;
  border: none;
  color: #ffffff;
  font-size: 14px;
  outline: none;
}

.language-input::placeholder {
  color: rgba(255, 255, 255, 0.5);
}

/* Step Actions */
.step-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.btn-back-step {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: rgba(255, 255, 255, 0.05);
  border: 2px solid rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  color: #ffffff;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-back-step:hover {
  background: rgba(255, 255, 255, 0.1);
}

.btn-next,
.btn-create {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: linear-gradient(135deg, var(--color-accent), var(--color-accent-2));
  border: none;
  border-radius: 10px;
  color: #ffffff;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 20px rgba(0, 255, 245, 0.4);
  margin-left: auto;
}

.btn-next:hover,
.btn-create:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 30px rgba(0, 255, 245, 0.6);
}

.btn-next:disabled,
.btn-create:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.btn-create {
  background: linear-gradient(135deg, var(--color-pink), var(--color-yellow));
  box-shadow: 0 4px 20px rgba(255, 0, 110, 0.4);
}

.btn-create:hover {
  box-shadow: 0 6px 30px rgba(255, 0, 110, 0.6);
}

/* Loader */
.loader-dots {
  display: flex;
  gap: 6px;
}

.loader-dots span {
  width: 8px;
  height: 8px;
  background: currentColor;
  border-radius: 50%;
  animation: dot-bounce 1.4s ease-in-out infinite;
}

.loader-dots span:nth-child(1) { animation-delay: -0.32s; }
.loader-dots span:nth-child(2) { animation-delay: -0.16s; }

@keyframes dot-bounce {
  0%, 80%, 100% { transform: scale(0); }
  40% { transform: scale(1); }
}

/* Review */
.review-grid {
  display: grid;
  gap: 20px;
  margin-bottom: 24px;
}

.review-section {
  padding: 20px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
}

.review-title {
  font-size: 14px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--color-accent);
  margin-bottom: 12px;
  font-family: var(--font-mono);
}

.review-item {
  display: flex;
  gap: 12px;
  margin-bottom: 8px;
}

.review-label {
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-muted);
  min-width: 100px;
}

.review-value {
  font-size: 14px;
  color: #ffffff;
  flex: 1;
}

/* Message Alert */
.message-alert {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 10px;
  font-size: 14px;
  margin-bottom: 24px;
  animation: fade-in 0.3s ease;
}

.message-alert.success {
  background: rgba(34, 197, 94, 0.15);
  border: 2px solid #4ade80;
  color: #4ade80;
}

.message-alert.error {
  background: rgba(239, 68, 68, 0.15);
  border: 2px solid #f87171;
  color: #f87171;
}

@keyframes fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Tips Card */
.tips-card {
  background: rgba(255, 255, 255, 0.05);
  border: 2px solid var(--color-yellow);
  border-radius: 20px;
  padding: 24px;
  backdrop-filter: blur(20px);
  box-shadow: 0 0 40px rgba(255, 190, 11, 0.3);
  position: sticky;
  top: 100px;
}

.tips-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
  color: var(--color-yellow);
  font-size: 16px;
  font-weight: 700;
}

.tips-header svg {
  filter: drop-shadow(0 0 8px var(--color-yellow));
}

.tips-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
}

.tip-item {
  display: flex;
  align-items: center;
  gap: 12px;
}

.tip-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  font-family: var(--font-mono);
  background: rgba(255, 255, 255, 0.05);
  border: 2px solid rgba(255, 255, 255, 0.2);
  color: var(--color-text-muted);
  flex-shrink: 0;
  transition: all 0.3s ease;
}

.tip-icon.active {
  background: rgba(34, 197, 94, 0.2);
  border-color: #4ade80;
  color: #4ade80;
  box-shadow: 0 0 15px rgba(74, 222, 128, 0.5);
}

.tip-icon-optional {
  background: rgba(255, 190, 11, 0.1);
  border-color: var(--color-yellow);
  color: var(--color-yellow);
}

.tip-text {
  font-size: 14px;
  color: #ffffff;
  flex: 1;
}

.tips-footer {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 14px;
  background: rgba(0, 255, 245, 0.1);
  border: 1px solid rgba(0, 255, 245, 0.3);
  border-radius: 10px;
  color: var(--color-accent);
  font-size: 12px;
  line-height: 1.5;
}

.tips-footer svg {
  flex-shrink: 0;
  margin-top: 2px;
}

/* Responsive */
@media (max-width: 1024px) {
  .content-grid {
    grid-template-columns: 1fr;
  }

  .tips-card {
    position: relative;
    top: 0;
  }
}

@media (max-width: 768px) {
  .stepper-header {
    padding: 16px;
  }

  .step-content {
    padding: 24px 16px;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .step-actions {
    flex-direction: column;
  }

  .btn-next,
  .btn-create,
  .btn-back-step {
    width: 100%;
    justify-content: center;
  }
}

@media (max-width: 600px) {
  .onboard-container {
    padding: 24px 16px;
  }

  .stepper-header {
    gap: 4px;
  }

  .step-item {
    min-width: 60px;
  }

  .step-label {
    font-size: 10px;
  }
}
</style>